import Classnames from "classnames";
import React, { useRef } from "react";

import Link from "@router/link";

import style from "./button.styl";

export type ButtonProps = {
  /**
   * element to render as parent, `<button>` by default
   */
  as?: string | React.ElementType;
  /**
   * Set which sides should have no border radius.
   *
   * Used to 'attach' buttons to each other or other components.
   *
   * Array of values. Values are one of: `top`, `right`, `bottom`, `left`
   *
   * @default []
   */
  attach?: Array<"top" | "right" | "bottom" | "left">;
  /**
   * Button background colour
   */
  bgColor?: string;
  /**
   * Button border colour
   */
  borderColor?: string;
  /**
   * Button border radius
   */
  borderRadius?: string;
  /**
   * HTML class
   */
  className?: string;
  /**
   * Disable button
   */
  disabled?: boolean;
  /**
   * Fill parent width
   */
  fill?: boolean;
  /**
   * Button is icon button
   */
  iconButton?: boolean;
  /**
   * Label to show when hovering on icon button
   */
  iconLabel?: string;
  /**
   * Icon button colours
   */
  iconColors?: boolean;
  /**
   * Loading state, shows spinner
   */
  loading?: boolean;
  /**
   * Text to show next to loading spinner
   */
  loadingText?: string;
  /**
   * Button click handler
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /**
   * Custom padding
   */
  padding?: string | number;
  /**
   * Button size
   *
   * @default 'large'
   */
  size?: "tiny" | "small" | "medium" | "large";
  /**
   * Button border radius
   *
   * @default 'rounded'
   */
  shape?: "square" | "rounded" | "circle";
  /**
   * Make normal button state have transparent background
   */
  transparent?: boolean;
  /**
   * Preset Button colour types
   *
   * @default 'primary'
   */
  type?: "primary" | "secondary" | "tertiary" | "danger" | "custom";
  /**
   * Button text colour
   */
  textColor?: string;
  /**
   * Data test id
   */
  "data-testid"?: string;
  children?: React.ReactNode;
  danger?: boolean;
  /**
   * @default 'inherit'
   */
  font?: string;
  height?: string;
  align?: string;
  href?: string;
  external?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
};

const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      as,
      disabled,
      iconButton,
      iconLabel,
      loading,
      onClick,
      href,
      attach = [],
      bgColor,
      borderColor,
      borderRadius,
      font = "inherit",
      height,
      padding,
      textColor,
      align,
      size = "large",
      type = "primary",
      shape = "rounded",
      fill,
      iconColors,
      danger,
      transparent,
      loadingText,
      className,
      children,
      external,
      onFocus,
      onBlur,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    let internalRef = useRef<HTMLElement>(null);
    if (ref) internalRef = ref as React.MutableRefObject<HTMLElement>;

    const buttonVariables: React.CSSProperties = {
      "--button-bg-color": bgColor || "var(--primary)",
      "--button-border-color": borderColor || "var(--primary)",
      "--button-border-radius": borderRadius || "0.5rem",
      "--button-font": font,
      "--button-height": height,
      "--button-padding": padding || "0 1.5rem",
      "--button-text-color": textColor || "white",
      "--button-align": align || "center",
    } as React.CSSProperties;

    let classNameMerged = Classnames(style.base, className);

    if (typeof as === "undefined") {
      classNameMerged = Classnames(
        style.button,
        style[size],
        style[type],
        style[shape],
        {
          [style.fill]: fill,
          [style.loading]: loading,
          [style.icon]: iconButton,
          [style.circle]: iconButton,
          [style.iconLabel]: iconLabel,
          [style.iconColors]: iconColors,
          [style.danger]: danger,
          [style.transparent]: transparent,
          [style.disabled]: disabled,
        },
        ...attach.map((attachItem) => style[attachItem]),
        classNameMerged,
      );
    }

    // Add loading spinner and text to children if loading status
    let childrenArray = React.Children.toArray(children);
    childrenArray = childrenArray.map((child, index) =>
      typeof child === "string" ? (
        <span key={`text-child-${index}`}>{child}</span>
      ) : (
        child
      ),
    );

    if (iconLabel) {
      childrenArray.push(
        <span key="icon-label" className={style.iconLabelText}>
          {iconLabel}
        </span>,
      );
    }

    if (loading) {
      childrenArray = [
        <span
          key="loader"
          className={Classnames(style.loadingSpinner, {
            [style.spinnerWithText]: loadingText,
          })}
        />,
        ...(loadingText
          ? [
              <span key="loading-text" className={style.loadingtext}>
                {loadingText}
              </span>,
            ]
          : childrenArray),
      ];
    }

    // Handle pressing enter and space even when button is rendered as a div etc
    const onKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
      const code = event.charCode || event.keyCode;
      if (code === 32 || code === 13) {
        event.preventDefault();
        (window.document.activeElement as HTMLElement)?.click();
      }
    };

    // Render as button by default if not overwritten
    const renderAs = typeof href !== "undefined" ? Link : as || "button";

    const onClickWithFocus = (e: React.MouseEvent<HTMLElement>) => {
      (e.target as HTMLElement).focus();
      if (typeof onMouseDown === "function") {
        onMouseDown(e);
      }
    };

    return React.createElement(
      renderAs,
      {
        disabled: disabled || loading,
        onClick,
        href,
        external: external ? "true" : undefined,
        onKeyPress,
        onFocus,
        onBlur,
        onMouseDown: onClickWithFocus,
        onMouseEnter,
        onMouseLeave,
        ref: internalRef,
        role: "button",
        style: typeof as === "undefined" ? buttonVariables : undefined,
        tabIndex: 0,
        className: classNameMerged,
        "data-testid": dataTestId,
      },
      childrenArray,
    );
  },
);

Button.displayName = "Button";

export default Button;
